/**
 * Carries "why am I on the login page" across the hard navigation that logout performs.
 *
 * `onLogout()` runs `localStorage.clear()`, and toasts neither survive `window.location`
 * nor render reliably (the app-wide container queues them). `sessionStorage` survives both
 * and is scoped to the tab that was logged out, which is exactly the tab that should see it.
 */

export const SESSION_ENDED_KEY = 'ielect.session-ended';

export type SessionEndedReason = 'expired';

export const SESSION_ENDED_MESSAGE: Record<SessionEndedReason, string> = {
	expired: 'Your session has ended. Please sign in again.',
};

export const writeSessionEndedNotice = (reason: SessionEndedReason) => {
	try {
		sessionStorage.setItem(SESSION_ENDED_KEY, reason);
	} catch {
		// Private mode / blocked storage — the user just gets the plain login page.
	}
};

/** Read-once: the notice shows on the first render of the login page and never again. */
export const consumeSessionEndedNotice = (): SessionEndedReason | null => {
	try {
		const raw = sessionStorage.getItem(SESSION_ENDED_KEY);
		if (!raw) return null;
		sessionStorage.removeItem(SESSION_ENDED_KEY);
		return raw in SESSION_ENDED_MESSAGE ? (raw as SessionEndedReason) : null;
	} catch {
		return null;
	}
};
